import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { pdf } from '@react-pdf/renderer';
import ExportPDFApplicationExternalForm from './ExportPDFApplicationExternalForm';

// eslint-disable-next-line react/prop-types
const ApplicationExternalForm = ({ booking_id }) => {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Marital Status
    maritalStatus: '',
    marriageRegistered: false,
    
    // Employer & Spouse
    employerName: '',
    employerDob: '',
    employerNric: '',
    employerPassport: '',
    
    // Type of Dwelling
    dwelling: [],
    levels: '1',
    bedrooms: '3',
    toilets: '0',
    
    // Residential Status
    resStatus: '',
    
    // Employer Information
    profession: '',
    company: '',
    officePhone: '',
    mobilePhone: '',
    address: '',
    email: '',
    homePhone: '',
    
    // Family Members
    familyMembers: [
      { name: '', relationship: '', id: '', dob: '' },
      { name: '', relationship: '', id: '', dob: '' }
    ],
    
    // Purpose of Application
    purpose: '',
    replaceWpNo: '',
    
    // FDW Information
    fdwName: '',
    fdwDob: '',
    fdwNationality: '',
    fdwPassport: '',
    fdwWp: '',
    fdwMarital: '',
    fdwEducation: '',
    fdwStatus: '',
    
    // Declarations
    income: '',
    
    // Signature
    signatureName: '',
    signatureId: '',
    signatureDate: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'marriageRegistered') {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      } else if (name === 'dwelling') {
        setFormData(prev => ({
          ...prev,
          dwelling: checked 
            ? [...(prev.dwelling || []), value]
            : (prev.dwelling || []).filter(item => item !== value)
        }));
      }
    } else if (type === 'radio') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFamilyMemberChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      familyMembers: prev.familyMembers.map((member, i) => 
        i === index ? { ...member, [field]: value } : member
      )
    }));
  };

  const addFamilyMember = () => {
    setFormData(prev => ({
      ...prev,
      familyMembers: [...prev.familyMembers, { name: '', relationship: '', id: '', dob: '' }]
    }));
  };

  const removeFamilyMember = (index) => {
    setFormData(prev => ({
      ...prev,
      familyMembers: prev.familyMembers.filter((_, i) => i !== index)
    }));
  };
const [agencyInfo, setAgencyInfo] = useState({});
  // Fetch existing application data
  const fetchApplicationData = async () => {
    if (!booking_id) return;
    
    setIsLoading(true);
    try {
      const response = await api.get(`external-forms/application-form/${booking_id}/`);
      if (response.data) {
          setAgencyInfo({
          agencyAddress: response.data.agency_address || '',
          agencyPhone: response.data.agency_phone || ''
        });
        setFormData({
          maritalStatus: response.data.marital_status || '',
          marriageRegistered: response.data.marriage_registered || false,
          employerName: response.data.employer_name || '',
          employerDob: response.data.employer_dob || '',
          employerNric: response.data.employer_nric || '',
          employerPassport: response.data.employer_passport || '',
          dwelling: response.data.dwelling || [],
          levels: response.data.levels?.toString() || '1',
          bedrooms: response.data.bedrooms?.toString() || '3',
          toilets: response.data.toilets?.toString() || '0',
          resStatus: response.data.res_status || '',
          profession: response.data.profession || '',
          company: response.data.company || '',
          officePhone: response.data.office_phone || '',
          mobilePhone: response.data.mobile_phone || '',
          address: response.data.address || '',
          email: response.data.email || '',
          homePhone: response.data.home_phone || '',
          familyMembers: response.data.family_members || [
            { name: '', relationship: '', id: '', dob: '' },
            { name: '', relationship: '', id: '', dob: '' }
          ],
          purpose: response.data.purpose || '',
          replaceWpNo: response.data.replace_wp_no || '',
          fdwName: response.data.fdw_name || '',
          fdwDob: response.data.fdw_dob || '',
          fdwNationality: response.data.fdw_nationality || '',
          fdwPassport: response.data.fdw_passport || '',
          fdwWp: response.data.fdw_wp || '',
          fdwMarital: response.data.fdw_marital || '',
          fdwEducation: response.data.fdw_education || '',
          fdwStatus: response.data.fdw_status || '',
          income: response.data.income || '',
          signatureName: response.data.signature_name || '',
          signatureId: response.data.signature_id || '',
          signatureDate: response.data.signature_date || ''
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.log('No existing application found for booking_id:', booking_id);
      } else {
        console.error('Error fetching application data:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchApplicationData();
  }, [booking_id]);

  const validateForm = () => {
    const newErrors = {};
    // All fields are optional
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setErrors({});
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const apiData = {
        booking: booking_id,
        marital_status: formData.maritalStatus,
        marriage_registered: formData.marriageRegistered,
        employer_name: formData.employerName,
        employer_dob: formData.employerDob || null,
        employer_nric: formData.employerNric,
        employer_passport: formData.employerPassport,
        dwelling: formData.dwelling,
        levels: parseInt(formData.levels) || 1,
        bedrooms: parseInt(formData.bedrooms) || 3,
        toilets: parseInt(formData.toilets) || 0,
        res_status: formData.resStatus,
        profession: formData.profession,
        company: formData.company,
        office_phone: formData.officePhone,
        mobile_phone: formData.mobilePhone,
        address: formData.address,
        email: formData.email,
        home_phone: formData.homePhone,
        family_members: formData.familyMembers,
        purpose: formData.purpose,
        replace_wp_no: formData.replaceWpNo,
        fdw_name: formData.fdwName,
        fdw_dob: formData.fdwDob || null,
        fdw_nationality: formData.fdwNationality,
        fdw_passport: formData.fdwPassport,
        fdw_wp: formData.fdwWp,
        fdw_marital: formData.fdwMarital,
        fdw_education: formData.fdwEducation,
        fdw_status: formData.fdwStatus,
        income: formData.income,
        signature_name: formData.signatureName,
        signature_id: formData.signatureId,
        signature_date: formData.signatureDate || null
      };

      const response = await api.post(`external-forms/application-form/${booking_id}/`, apiData);
      console.log('Application submitted successfully:', response.data);
      alert('Application form submitted successfully!');
      
    } catch (error) {
      console.error('Error submitting application:', error);
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          setErrors(error.response.data);
        } else {
          alert('Error submitting form: ' + error.response.data);
        }
      } else {
        alert('Error submitting form. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const originalHandleSubmit = (e) => {
    e.preventDefault();
    console.log('Application Form Data:', formData);
    // Handle form submission here
  };

  const handleReset = () => {
    setFormData({
      maritalStatus: '',
      marriageRegistered: false,
      employerName: '',
      employerDob: '',
      employerNric: '',
      employerPassport: '',
      dwelling: [],
      levels: '1',
      bedrooms: '3',
      toilets: '0',
      resStatus: '',
      profession: '',
      company: '',
      officePhone: '',
      mobilePhone: '',
      address: '',
      email: '',
      homePhone: '',
      familyMembers: [
        { name: '', relationship: '', id: '', dob: '' },
        { name: '', relationship: '', id: '', dob: '' }
      ],
      purpose: '',
      replaceWpNo: '',
      fdwName: '',
      fdwDob: '',
      fdwNationality: '',
      fdwPassport: '',
      fdwWp: '',
      fdwMarital: '',
      fdwEducation: '',
      fdwStatus: '',
      income: '',
      signatureName: '',
      signatureId: '',
      signatureDate: ''
    });
  };

  const handleDelete = async () => {
    if (!booking_id) {
      alert('No booking ID provided for deletion');
      return;
    }

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this Application External Form record? This action cannot be undone.'
    );

    if (!confirmDelete) {
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Deleting Application External Form for booking_id:', booking_id);
      const response = await api.delete(`external-forms/application-form/${booking_id}/`);
      console.log('Application External Form deleted successfully:', response.data);
      
      // Show success message
      alert('Application External Form record deleted successfully!');
      
      // Reset the form after deletion
      handleReset();
      
    } catch (error) {
      console.error('Error deleting Application External Form:', error);
      alert('Application External Form record deleted successfully!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { user } = useAuth();
  const handleDownloadPDF = async () => {
    setIsSubmitting(true);

    try {
      console.log('Generating Application External Form PDF for booking_id:', booking_id);
      
      // Prepare the data for PDF generation
      const pdfData = {
        ...formData,
        booking_id: booking_id
      };
alert(JSON.stringify(user))
      // Generate PDF using React PDF
      const blob = await pdf(
        <ExportPDFApplicationExternalForm
          user={user}
          data={pdfData}
          agencyInfo={agencyInfo}
        />
      ).toBlob();
      
      // Create blob link to download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const today = new Date().toISOString().split('T')[0];
      const filename = booking_id 
        ? `Application_External_Form_${booking_id}_${today}.pdf`
        : `Application_External_Form_${today}.pdf`;
      link.setAttribute('download', filename);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Application External Form PDF generated and downloaded successfully');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
   
      <div className="page-wrapper" style={{ marginLeft: '0' }}>
        <div className="content container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="content-page-header">
                    <h5>Application Form</h5>
                  </div>

                  {/* Loading Indicator */}
                  {isLoading && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <div className="spinner-border text-primary me-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span className="text-muted">Loading existing application data...</span>
                    </div>
                  )}

                  {/* Form Content - Hidden during loading */}
                  <div style={{ display: isLoading ? 'none' : 'block' }}>
                  <form onSubmit={handleSubmit}>
                    {/* Marital Status Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Marital Status</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {['Single', 'Married', 'Divorced', 'Widowed'].map((status) => (
                            <div key={status} className="col-lg-3 col-6">
                              <div className="form-check mb-2">
                                <input
                                  type="radio"
                                  name="maritalStatus"
                                
                                  value={status}
                                  checked={formData.maritalStatus === status}
                                  onChange={handleInputChange}
                                  className="form-check-input"
                                  id={`marital-${status}`}
                                />
                                <label className="form-check-label" htmlFor={`marital-${status}`}>
                                  {status}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="form-check mt-3">
                          <input
                            type="checkbox"
                            name="marriageRegistered"
                            checked={formData.marriageRegistered}
                            onChange={handleInputChange}
                            className="form-check-input"
                            id="marriageRegistered"
                          />
                          <label className="form-check-label" htmlFor="marriageRegistered">
                            Marriage registered in Singapore
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Employer & Spouse Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Employer & Spouse</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Name</label>
                              <input
                                type="text"
                                name="employerName"
                                value={formData.employerName}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Date of Birth</label>
                              <input
                                type="date"
                                name="employerDob"
                                value={formData.employerDob}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>NRIC/FIN No</label>
                              <input
                                type="text"
                                name="employerNric"
                                value={formData.employerNric}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Passport No</label>
                              <input
                                type="text"
                                name="employerPassport"
                                value={formData.employerPassport}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Type of Dwelling Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Type of Dwelling</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {[
                            'Bungalow', 'Condominium', 'HDB Maisonette', 'Landed Semi-D', 
                            'Private Apartment', 'HDB Executive', 'Landed Terrace', 'Others', 'HDB'
                          ].map((dwellingType) => (
                            <div key={dwellingType} className="col-lg-3 col-6">
                              <div className="form-check mb-2">
                                <input
                                  type="checkbox"
                                  name="dwelling"
                                  value={dwellingType}
                                  checked={(formData.dwelling || []).includes(dwellingType)}
                                  onChange={handleInputChange}
                                  className="form-check-input"
                                  id={`dwelling-${dwellingType}`}
                                />
                                <label className="form-check-label" htmlFor={`dwelling-${dwellingType}`}>
                                  {dwellingType}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="row mt-3">
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Levels</label>
                              <input
                                type="number"
                                name="levels"
                                value={formData.levels}
                                onChange={handleInputChange}
                                className="form-control"
                                min="0"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Bedrooms</label>
                              <input
                                type="number"
                                name="bedrooms"
                                value={formData.bedrooms}
                                onChange={handleInputChange}
                                className="form-control"
                                min="0"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Toilets</label>
                              <input
                                type="number"
                                name="toilets"
                                value={formData.toilets}
                                onChange={handleInputChange}
                                className="form-control"
                                min="0"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Residential Status Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Residential Status</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {['Singaporean', 'PR', 'EP/DP', 'Diplomat', 'LTVP/SVP', 'Others'].map((status) => (
                            <div key={status} className="col-lg-3 col-6">
                              <div className="form-check mb-2">
                                <input
                                  type="radio"
                                  name="resStatus"
                                  value={status}
                                  checked={formData.resStatus === status}
                                  onChange={handleInputChange}
                                  className="form-check-input"
                                  id={`res-${status}`}
                                />
                                <label className="form-check-label" htmlFor={`res-${status}`}>
                                  {status}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Employer Information Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Employer Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Profession</label>
                              <input
                                type="text"
                                name="profession"
                                value={formData.profession}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Employer/Company</label>
                              <input
                                type="text"
                                name="company"
                                value={formData.company}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Office Phone</label>
                              <input
                                type="text"
                                name="officePhone"
                                value={formData.officePhone}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Mobile Phone</label>
                              <input
                                type="text"
                                name="mobilePhone"
                                value={formData.mobilePhone}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="input-block mb-3">
                          <label>Address</label>
                          <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>

                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Email</label>
                              <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Home Phone</label>
                              <input
                                type="text"
                                name="homePhone"
                                value={formData.homePhone}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Family Members Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">Family Members</h6>
                          <button
                            type="button"
                            onClick={addFamilyMember}
                            className="btn btn-sm btn-primary"
                          >
                            Add Member
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Relationship</th>
                                <th>NRIC/PP</th>
                                <th>Date of Birth</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {formData.familyMembers.map((member, index) => (
                                <tr key={index}>
                                  <td>
                                    <input
                                      type="text"
                                      value={member.name}
                                      onChange={(e) => handleFamilyMemberChange(index, 'name', e.target.value)}
                                      className="form-control form-control-sm"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={member.relationship}
                                      onChange={(e) => handleFamilyMemberChange(index, 'relationship', e.target.value)}
                                      className="form-control form-control-sm"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={member.id}
                                      onChange={(e) => handleFamilyMemberChange(index, 'id', e.target.value)}
                                      className="form-control form-control-sm"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="date"
                                      value={member.dob}
                                      onChange={(e) => handleFamilyMemberChange(index, 'dob', e.target.value)}
                                      className="form-control form-control-sm"
                                    />
                                  </td>
                                  <td>
                                    {formData.familyMembers.length > 2 && (
                                      <button
                                        type="button"
                                        onClick={() => removeFamilyMember(index)}
                                        className="btn btn-sm btn-danger"
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Purpose of Application Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Purpose of Application</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {['A new FDW', 'A replacement', 'An additional FDW'].map((purpose) => (
                            <div key={purpose} className="col-lg-4 col-12">
                              <div className="form-check mb-2">
                                <input
                                  type="radio"
                                  name="purpose"
                                  value={purpose}
                                  checked={formData.purpose === purpose}
                                  onChange={handleInputChange}
                                  className="form-check-input"
                                  id={`purpose-${purpose}`}
                                />
                                <label className="form-check-label" htmlFor={`purpose-${purpose}`}>
                                  {purpose}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="input-block mb-3 mt-3">
                          <label>Work Permit No. of FDW to be replaced</label>
                          <input
                            type="text"
                            name="replaceWpNo"
                            value={formData.replaceWpNo}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    {/* FDW Information Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">FDW Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Name</label>
                              <input
                                type="text"
                                name="fdwName"
                                value={formData.fdwName}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Date of Birth</label>
                              <input
                                type="date"
                                name="fdwDob"
                                value={formData.fdwDob}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Nationality</label>
                              <input
                                type="text"
                                name="fdwNationality"
                                value={formData.fdwNationality}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Passport No</label>
                              <input
                                type="text"
                                name="fdwPassport"
                                value={formData.fdwPassport}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Work Permit No</label>
                              <input
                                type="text"
                                name="fdwWp"
                                value={formData.fdwWp}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Marital Status</label>
                              <input
                                type="text"
                                name="fdwMarital"
                                value={formData.fdwMarital}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="input-block mb-3">
                          <label>Level of Education</label>
                          <input
                            type="text"
                            name="fdwEducation"
                            value={formData.fdwEducation}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>

                        <label className="mb-2">FDW Status</label>
                        <div className="row">
                          {['In Singapore', 'Not in Singapore'].map((status) => (
                            <div key={status} className="col-lg-6 col-12">
                              <div className="form-check mb-2">
                                <input
                                  type="radio"
                                  name="fdwStatus"
                                  value={status}
                                  checked={formData.fdwStatus === status}
                                  onChange={handleInputChange}
                                  className="form-check-input"
                                  id={`fdw-status-${status}`}
                                />
                                <label className="form-check-label" htmlFor={`fdw-status-${status}`}>
                                  {status}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Declarations Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Declarations</h6>
                      </div>
                      <div className="card-body">
                        <p className="text-muted small mb-3">
                          Documents required depending on employer type (Citizen/PR or Foreign Employer).<br />
                          Income declaration options included.
                        </p>
                        
                        <div className="input-block mb-3">
                          <label>Annual household income (S$)</label>
                          <input
                            type="number"
                            name="income"
                            value={formData.income}
                            onChange={handleInputChange}
                            className="form-control"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Signature Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Signature</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Name</label>
                              <input
                                type="text"
                                name="signatureName"
                                value={formData.signatureName}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>NRIC/PP No</label>
                              <input
                                type="text"
                                name="signatureId"
                                value={formData.signatureId}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Date</label>
                              <input
                                type="date"
                                name="signatureDate"
                                value={formData.signatureDate}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Form Buttons */}
                    <div className="row">
                      <div className="col-12">
                        <div className="btn-path text-end">
                          <button
                            type="button"
                            onClick={handleReset}
                            className="btn btn-cancel bg-primary-light me-3"
                            disabled={isSubmitting}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            onClick={handleDelete}
                            className="btn btn-danger me-3"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Deleting...' : 'Clean'}
                          </button>
                          <button
                            type="button"
                            onClick={handleDownloadPDF}
                            className="btn btn-success me-3"
                            disabled={isSubmitting}
                            title="Download PDF of current form data"
                          >
                            {isSubmitting ? 'Generating...' : (
                              <>
                                <i className="fas fa-download me-2"></i>
                                Download PDF
                              </>
                            )}
                          </button>
                          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                          </button>
                        </div>
                      </div>
                    </div>
                    {errors.general && (
                      <div className="alert alert-danger mt-3">{errors.general}</div>
                    )}
                  </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApplicationExternalForm;